/**
 * Seed song library.
 *
 * MOCK/FIXTURE DATA. Stands in for what `GET /songs` will return. Moved out of
 * the monolith unchanged apart from being wrapped in a factory (see below).
 *
 * Song ordering
 * -------------
 * Song ids are opaque UUID strings (see src/lib/ids.ts), so they can no longer
 * double as a sort key the way the old incrementing integers did. `seq` is an
 * explicit, monotonically increasing creation counter used purely for ordering.
 *
 * This also corrects the sort direction. The seed songs run from newest date
 * (Jul 27) to oldest (Jul 19), so the original `b.id - a.id` comparison for
 * "Newest first" actually surfaced the *oldest* song first. Descending seq
 * values put them in true chronological order.
 *
 * Dates remain display strings ("Jul 27", "Just now"). Converting them to real
 * timestamps is deferred until the backend supplies ISO dates — see
 * docs/api-contract.md, "Songs".
 *
 * EVERY SEED SONG IS PRIVATE, and none of them may be changed to public here.
 * ------------------------------------------------------------------------
 * `visibility: 'private'` on all four is a deliberate refusal, not an oversight. These
 * stand in for songs a real user made, and nothing in the data says that user chose to
 * publish any of them — so marking one `'public'` would be this file inventing a
 * publication decision on their behalf, and it would immediately show up as "other
 * creators' music" on the Home page.
 *
 * The visible consequence is that the public catalogue is empty in this build. That is
 * the correct outcome and the Home page states it; see lib/discovery and
 * api/discoveryApi.describeDiscoveryBlockers(). If you are tempted to flip one of these to
 * populate the catalogue for a screenshot, don't — that is exactly the fabrication the
 * separation of `status` from `visibility` exists to prevent.
 */
import { newId } from '../lib/ids';
import { generateLyrics } from '../lib/music';
import type { Song } from '../types/domain';

/**
 * Builds the seed library.
 *
 * A factory rather than a module-level constant because it calls newId(): a
 * shared frozen array would hand the same ids to every consumer, and tests need
 * a fresh, isolated library per case rather than one mutated across files.
 */
export function createInitialSongs(): Song[] {
  return [
    {
      id: newId(),
      seq: 4,
      title: 'Summer highway',
      genre: 'Acoustic pop',
      length: '3:12',
      date: 'Jul 27',
      createdAt: '2026-07-27T00:00:00.000Z',
      lyrics: generateLyrics('a nostalgic summer road trip with old friends', 'Acoustic pop'),
      prompt: 'A nostalgic summer road trip with old friends',
      favorite: true,
      status: 'ready',
      visibility: 'private',
      bpm: 96,
      mood: 'Nostalgic',
      language: 'English',
      aiModel: 'Lyrixsa v1',
      creditsUsed: 1,
      downloadCount: 0,
      versions: [
        {
          id: newId(),
          label: 'Original',
          kind: 'original',
          date: 'Jul 27',
          length: '3:12',
          prompt: 'A nostalgic summer road trip with old friends',
        },
      ],
    },
    {
      id: newId(),
      seq: 3,
      title: 'Midnight drive',
      genre: 'Synthwave',
      length: '2:48',
      date: 'Jul 25',
      createdAt: '2026-07-25T00:00:00.000Z',
      lyrics: generateLyrics('a lonely late night drive through the city', 'Synthwave'),
      prompt: 'A lonely late night drive through the city',
      favorite: false,
      status: 'ready',
      visibility: 'private',
      bpm: 118,
      mood: 'Restless',
      language: 'English',
      aiModel: 'Lyrixsa v1',
      creditsUsed: 1,
      downloadCount: 0,
      versions: [
        {
          id: newId(),
          label: 'Original',
          kind: 'original',
          date: 'Jul 25',
          length: '2:48',
          prompt: 'A lonely late night drive through the city',
        },
      ],
    },
    {
      id: newId(),
      seq: 2,
      title: 'Golden hour',
      genre: 'Lo-fi instrumental',
      length: '4:05',
      date: 'Jul 22',
      createdAt: '2026-07-22T00:00:00.000Z',
      lyrics: generateLyrics('a quiet golden hour walk', 'Lo-fi instrumental'),
      prompt: 'A quiet golden hour walk',
      favorite: false,
      status: 'ready',
      visibility: 'private',
      bpm: 78,
      mood: 'Dreamy',
      language: 'Instrumental',
      aiModel: 'Lyrixsa v1',
      creditsUsed: 1,
      downloadCount: 0,
      versions: [
        {
          id: newId(),
          label: 'Original',
          kind: 'original',
          date: 'Jul 22',
          length: '4:05',
          prompt: 'A quiet golden hour walk',
        },
      ],
    },
    {
      id: newId(),
      seq: 1,
      title: 'Neon skyline',
      genre: 'Synthwave',
      length: '3:34',
      date: 'Jul 19',
      createdAt: '2026-07-19T00:00:00.000Z',
      lyrics: generateLyrics('a neon lit skyline at night', 'Synthwave'),
      prompt: 'A neon lit skyline at night',
      favorite: true,
      status: 'ready',
      visibility: 'private',
      bpm: 124,
      mood: 'Euphoric',
      language: 'English',
      aiModel: 'Lyrixsa v1',
      creditsUsed: 1,
      downloadCount: 0,
      versions: [
        {
          id: newId(),
          label: 'Original',
          kind: 'original',
          date: 'Jul 19',
          length: '3:34',
          prompt: 'A neon lit skyline at night',
        },
      ],
    },
  ];
}

/** How many seed songs exist, so the seq counter can continue above them. */
export const INITIAL_SONG_COUNT = 4;
